import type { OrganizationApiKeyData } from '@openx/types/organization';
import type { AllTableCriteria } from '@openx/types/tableCriteria';
import { clientSideCriteriaProcessing } from '@openx/utils/lib/clientSideCriteriaProcessing';
import { isPast } from 'date-fns';
import { useMemo } from 'react';

type UseGetApiKeysTableDataProps = {
  activeTablePage: number;
  apiKeys: OrganizationApiKeyData[];
  criteria: AllTableCriteria;
  showActiveKeys?: boolean;
};

export const useGetApiKeysTableData = ({
  activeTablePage,
  apiKeys,
  criteria,
  showActiveKeys,
}: UseGetApiKeysTableDataProps) => {
  const filteredApiKeys = useMemo(() => {
    return apiKeys?.filter(apiKey => {
      if (!apiKey.expires_at) {
        return !!showActiveKeys;
      }

      const expirationDate = new Date(apiKey.expires_at);

      return showActiveKeys ? !isPast(expirationDate) : isPast(expirationDate);
    });
  }, [apiKeys, showActiveKeys]);

  const apiKeysWithAppliedCriteria = useMemo(() => {
    return clientSideCriteriaProcessing(filteredApiKeys ?? [], criteria);
  }, [filteredApiKeys, criteria]);

  const apiKeysToDisplayInTable = useMemo(() => {
    return apiKeysWithAppliedCriteria.slice(0, (activeTablePage + 1) * 10);
  }, [apiKeysWithAppliedCriteria, activeTablePage]);

  const shouldDisplayLoadMoreButton = useMemo(() => {
    return apiKeysWithAppliedCriteria.length > apiKeysToDisplayInTable.length;
  }, [apiKeysWithAppliedCriteria, apiKeysToDisplayInTable.length]);

  return {
    apiKeysToDisplayInTable,
    shouldDisplayLoadMoreButton,
  };
};
